'use client'
import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { useSession, signIn, signOut } from 'next-auth/react'
import { getPropertyCode } from '@/utils/propertyManager'
import { useMembership } from '@/contexts/MembershipContext'
import MemberLoginModal from './MemberLoginModal'

const FloatingLoginButton = () => {
  const router = useRouter()
  const { data: session, status } = useSession()
  const { 
    hasPropertyMembership, 
    memberData, 
    getMemberLevel, 
    isLoading: membershipLoading,
    handleManualLoginSuccess,
    handleManualLogout,
    isManuallyLoggedIn,
    getCurrentUserEmail
  } = useMembership()
  const [isExpanded, setIsExpanded] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)
  const [showMemberLoginModal, setShowMemberLoginModal] = useState(false)
  const isLoaded = status !== 'loading'
  const isSignedIn = !!session || isManuallyLoggedIn()
  const memberLevel = getMemberLevel()

  // Handle member registration
  const handleRegisterMember = () => {
    const propertyCode = getPropertyCode() || 'DEMO'
    const registerUrl = `https://member.emerald-system.id/register?propertycode=${propertyCode}`
    window.open(registerUrl, '_blank')
  }

  // Handle member login success
  const onMemberLoginSuccess = (memberData) => {
    handleManualLoginSuccess(memberData)
    setIsExpanded(false) // Collapse the menu after successful login
    
    // Force UI refresh by triggering a state update
    setTimeout(() => {
      // Force expanded state to show the user name
      setIsExpanded(true)
    }, 500)
  }

  // Handle logout for both next-auth and manual login
  const handleLogout = () => {
    if (isManuallyLoggedIn()) {
      handleManualLogout()
    } else {
      signOut()
    }
    setIsExpanded(false) // Collapse the menu after logout
  }

  // Auto-hide tooltip after 3 seconds
  useEffect(() => {
    if (showTooltip) {
      const timer = setTimeout(() => setShowTooltip(false), 3000)
      return () => clearTimeout(timer)
    }
  }, [showTooltip])

  // Listen for show-login-modal event (from promo banner)
  useEffect(() => {
    const handleShowLoginModal = () => {
      setIsExpanded(true)
    }
    window.addEventListener('show-login-modal', handleShowLoginModal)
    return () => window.removeEventListener('show-login-modal', handleShowLoginModal)
  }, [])

  // Don't render until session is loaded
  if (!isLoaded) return null

  return (
    <div className="fixed bottom-6 right-6 z-50">
      {/* Tooltip */}
      {showTooltip && !isSignedIn && (
        <div className="absolute bottom-16 right-0 bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-2.5 rounded-xl text-sm whitespace-nowrap shadow-xl transition-all duration-300 opacity-100 transform translate-x-0 scale-100 border border-white/10">
          Join our membership program or register as a new member!
          <div className="absolute -bottom-1 right-6 w-2.5 h-2.5 bg-gray-800 rotate-45 border-r border-b border-white/10"></div>
        </div>
      )}

      {/* Main Button */}
      <div
        className="relative"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        {isSignedIn ? (
          // User is signed in - show user info with member level if available
          <div className="flex items-center gap-3">
            {isExpanded && (
              <div className="bg-white rounded-full px-4 py-2 shadow-lg border border-gray-200 transition-all duration-300 opacity-100 transform translate-x-0">
                <div className="flex items-center gap-2">
                  <div className="text-left">
                    <div className="text-sm font-medium text-gray-700">
                      Welcome, {isManuallyLoggedIn() ? memberData?.first_name || memberData?.name || memberData?.email?.split('@')[0] : session?.user?.name?.split(' ')[0] || 'Member'}!
                    </div>
                    {memberLevel && (
                      <div className="text-xs text-gray-500 flex items-center gap-1">
                        <div 
                          className="w-2 h-2 rounded-full" 
                          style={{ backgroundColor: memberLevel.color }}
                        ></div>
                        {memberLevel.level}
                        {memberLevel.depositBalance > 0 && (
                          <span className="ml-1 text-green-600 font-medium">
                            • Deposit: Rp {new Intl.NumberFormat('id-ID').format(memberLevel.depositBalance)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => router.push('/membership')}
                      className="text-xs bg-emerald-500 hover:bg-emerald-600 text-white px-2 py-1 rounded transition-colors flex items-center justify-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                      </svg>
                      My Membership
                    </button>
                    <button
                      onClick={handleLogout}
                      className="text-xs bg-red-500 hover:bg-red-600 text-white px-2 py-1 rounded transition-colors"
                    >
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            <div
              className="bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full p-3 shadow-lg cursor-pointer hover:shadow-xl hover:scale-105 transition-all duration-300 flex items-center justify-center"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              <div className="relative">
                {session?.user?.image && !isManuallyLoggedIn() ? (
                  <img
                    src={session.user.image}
                    alt={session.user.name || 'User'}
                    className="w-8 h-8 rounded-full"
                  />
                ) : (
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                )}
                {memberLevel && (
                  <div 
                    className="absolute -top-1 -right-1 w-3 h-3 rounded-full border-2 border-white"
                    style={{ backgroundColor: memberLevel.color }}
                    title={`${memberLevel.level} Member`}
                  ></div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // User is not signed in - show login options
          <div className="flex items-center gap-3">
            {isExpanded && (
              <div className="flex flex-col gap-2.5 transition-all duration-300 opacity-100 transform translate-x-0">
                <button 
                  onClick={() => signIn('google')}
                  className="group flex items-center gap-3 bg-white pl-2.5 pr-5 py-2.5 rounded-2xl shadow-lg shadow-black/5 border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-50 group-hover:bg-blue-50 transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </span>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">
                    Sign in with Google
                  </span>
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                
                <button 
                  onClick={() => signIn('yahoo')}
                  className="group flex items-center gap-3 bg-white pl-2.5 pr-5 py-2.5 rounded-2xl shadow-lg shadow-black/5 border border-gray-100 hover:border-gray-200 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200"
                >
                  <span className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-50 group-hover:bg-purple-50 transition-colors">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#6001D2" d="M12 0c6.627 0 12 5.373 12 12s-5.373 12-12 12S0 18.627 0 12 5.373 0 12 0zm0 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm0 3.5l3.5 5.5-1.5 2.5h-4l-1.5-2.5L12 5.5zm0 1.5L9.5 11l1 1.5h3l1-1.5L12 7z"/>
                    </svg>
                  </span>
                  <span className="text-sm font-semibold text-gray-700 group-hover:text-gray-900 transition-colors">
                    Sign in with Yahoo
                  </span>
                  <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            )}

            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="relative flex items-center justify-center w-14 h-14 rounded-full bg-white shadow-xl ring-4 ring-white/50 hover:scale-110 active:scale-95 transition-all duration-300"
              style={{ boxShadow: '0 8px 28px rgba(0,0,0,0.18)' }}
              aria-label={isExpanded ? 'Close login options' : 'Open login options'}
            >
              {isExpanded ? (
                <svg
                  className="w-6 h-6 text-gray-500 transition-transform duration-200"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-7 h-7" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              
              {/* Pulse animation */}
              <span className="absolute inset-0 rounded-full animate-ping bg-blue-500/20 pointer-events-none"></span>
            </button>
          </div>
        )}
      </div>

      
      {/* Member Login Modal */}
      <MemberLoginModal 
        isOpen={showMemberLoginModal} 
        onClose={() => setShowMemberLoginModal(false)}
        onLoginSuccess={onMemberLoginSuccess}
      />
    </div>
  )
}

export default FloatingLoginButton
